import React, { useState, useEffect, useRef } from 'react';
import {
  X, MapPin, Briefcase, Calendar, Clock, MessageSquare, Music, Gamepad2,
  Instagram, Linkedin, Heart, Star, Zap, Award,
  ExternalLink, Headphones, Eye, Shield, Crown, Coffee, Sparkles,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { getImageUrl } from '../utils/imageUtils';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatMs = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

const ROLE_META = {
  superadmin: { label: 'Super Admin', icon: Crown,  hex: '#F59E0B' },
  executive:  { label: 'Executive',   icon: Shield, hex: '#8A2BE2' },
  admin:      { label: 'Admin',       icon: Shield, hex: '#00E5FF' },
  manager:    { label: 'Manager',     icon: Star,   hex: '#10B981' },
  staff:      { label: 'Staff',       icon: Coffee, hex: '#94A3B8' },
};

const ACT_ICON = { playing: Gamepad2, listening: Headphones, watching: Eye, custom: Sparkles };

const StaffProfileCard = ({ open, onClose, targetUsername, getAuthHeaders, onMessageClick }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [roblox, setRoblox] = useState(null);
  const [localProgress, setLocalProgress] = useState(0);
  const fetchedAtRef = useRef(0);

  useEffect(() => {
    if (!open || !targetUsername) return;
    (async () => {
      setLoading(true);
      try {
        const h = getAuthHeaders();
        const [p, s, r] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/profile/${targetUsername}`, { headers: h }),
          axios.get(`${BACKEND_URL}/api/spotify/now-playing/${targetUsername}`, { headers: h }).catch(() => ({ data: { connected: false } })),
          axios.get(`${BACKEND_URL}/api/roblox/presence/${targetUsername}`, { headers: h }).catch(() => ({ data: { connected: false } })),
        ]);
        setProfile(p.data);
        setNowPlaying(s.data);
        setRoblox(r.data);
        fetchedAtRef.current = Date.now();
        if (s.data?.track?.progress_ms != null) setLocalProgress(s.data.track.progress_ms);
      } catch { toast.error('Could not load profile'); onClose(); }
      finally { setLoading(false); }
    })();
  }, [open, targetUsername]);

  // Poll spotify every 5s
  useEffect(() => {
    if (!open || !targetUsername) return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/spotify/now-playing/${targetUsername}`, { headers: getAuthHeaders() });
        setNowPlaying(r.data);
        fetchedAtRef.current = Date.now();
        if (r.data?.track?.progress_ms != null) setLocalProgress(r.data.track.progress_ms);
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [open, targetUsername]);

  // Smooth local progress ticker
  useEffect(() => {
    if (!nowPlaying?.is_playing || !nowPlaying?.track) return;
    const t = setInterval(() => {
      const elapsed = Date.now() - fetchedAtRef.current;
      const base = nowPlaying.track.progress_ms || 0;
      const cap = nowPlaying.track.duration_ms || 0;
      setLocalProgress(Math.min(cap, base + elapsed));
    }, 500);
    return () => clearInterval(t);
  }, [nowPlaying?.is_playing, nowPlaying?.track?.name, nowPlaying?.track?.progress_ms]);

  if (!open) return null;

  const role = ROLE_META[profile?.role] || ROLE_META.staff;
  const RoleIcon = role.icon;
  const socials = profile?.socials || {};
  const isSpotify = nowPlaying?.is_playing && nowPlaying?.track;
  const spotifyConnected = nowPlaying?.connected || profile?.spotify_connected;
  const roConnected = roblox?.connected || profile?.roblox_connected;
  const roInGame = roblox?.presence?.status === 'in_game';
  const roOnline = roblox?.presence?.status === 'online';
  const activity = profile?.current_activity;
  const hasActivity = activity && (activity.title || activity.detail);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="p-0 bg-transparent border-0 shadow-none max-w-md" data-testid="staff-profile-card">
        <DialogTitle className="sr-only">Staff profile · {profile?.display_name || targetUsername}</DialogTitle>

        {loading ? (
          <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#0a0a14] to-[#0d0a1c] p-14 text-center">
            <div className="pointer-events-none absolute -top-24 -left-24 w-64 h-64 rounded-full blur-[110px]" style={{ background: 'rgba(0,229,255,0.16)' }} />
            <div className="relative">
              <div className="w-10 h-10 mx-auto rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
              <p className="text-white/50 text-xs mt-4 tracking-[0.28em] uppercase">Loading profile…</p>
            </div>
          </div>
        ) : profile ? (
          <div className="relative overflow-hidden rounded-[28px] border border-white/[0.08] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
            style={{ background: 'linear-gradient(140deg, rgba(10,10,20,0.95) 0%, rgba(15,10,25,0.98) 55%, rgba(8,12,24,0.95) 100%)' }}>

            {/* ═══ Banner ═══ */}
            <div className="relative h-32 overflow-hidden">
              {profile.banner_url ? (
                <img src={getImageUrl(profile.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : isSpotify && nowPlaying.track.album_art ? (
                <>
                  <img src={nowPlaying.track.album_art} alt="" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-70" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(140deg, rgba(10,10,20,0.55), rgba(15,10,25,0.75))' }} />
                </>
              ) : (
                <>
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${role.hex}44, rgba(138,43,226,0.35))` }} />
                  <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 rounded-full blur-[110px]" style={{ background: 'rgba(0,229,255,0.35)' }} />
                  <div className="pointer-events-none absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-[110px]" style={{ background: 'rgba(138,43,226,0.35)' }} />
                </>
              )}
              <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,229,255,0.55), transparent)' }} />
              <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/50 to-transparent" />
              <button onClick={onClose} data-testid="close-profile-btn"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-black/70 border border-white/[0.10] transition-all z-10">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* ═══ Avatar + name ═══ */}
            <div className="relative px-5 -mt-14">
              <div className="relative w-24 h-24 rounded-[22px] border-[3px] border-[#0a0a14] overflow-hidden bg-slate-900"
                style={{ boxShadow: `0 12px 30px -6px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05), 0 0 22px -6px ${role.hex}` }}>
                {profile.avatar ? (
                  <img src={getImageUrl(profile.avatar)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${role.hex}44, rgba(138,43,226,0.35))` }}>
                    <span className="text-white text-3xl font-light">{(profile.display_name || profile.username)?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                )}
                <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-[3px] border-[#0a0a14] ${profile.is_online ? 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]' : 'bg-slate-500'}`} />
              </div>

              <div className="mt-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-white text-xl font-light tracking-tight">{profile.display_name || profile.username}</h2>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border"
                    style={{ color: role.hex, background: `${role.hex}15`, borderColor: `${role.hex}40` }}>
                    <RoleIcon className="w-2.5 h-2.5" /> {role.label}
                  </span>
                </div>
                <p className="text-white/40 text-[13px]">@{profile.username}</p>
                {profile.custom_status && (
                  <p className="text-white/70 text-xs mt-1.5 italic">"{profile.custom_status}"</p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="mx-5 mt-4 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)' }} />

            {/* ═══ Content ═══ */}
            <div className="px-5 py-4 space-y-3 max-h-[52vh] overflow-y-auto jewel-scroll">

              {/* Spotify Now-Playing */}
              {isSpotify && (
                <div className="relative overflow-hidden rounded-2xl border border-[#1db954]/30 p-3"
                  style={{ background: 'linear-gradient(135deg, rgba(29,185,84,0.14), rgba(15,10,25,0.4))' }} data-testid="spotify-now-playing">
                  {nowPlaying.track.album_art && (
                    <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-40 blur-[70px]"
                      style={{ background: `url(${nowPlaying.track.album_art}) center/cover` }} />
                  )}
                  <div className="relative flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1db954] fill-current"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                      <p className="text-[9px] tracking-[0.24em] uppercase font-semibold text-[#1db954]">Listening on Spotify</p>
                    </div>
                    <div className="flex items-end gap-[3px] h-3.5">
                      <span className="w-[3px] bg-[#1db954] rounded-t-sm animate-[eq_0.9s_ease-in-out_infinite]" style={{ animationDelay: '0ms' }} />
                      <span className="w-[3px] bg-[#1db954] rounded-t-sm animate-[eq_0.9s_ease-in-out_infinite]" style={{ animationDelay: '150ms' }} />
                      <span className="w-[3px] bg-[#1db954] rounded-t-sm animate-[eq_0.9s_ease-in-out_infinite]" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                  <div className="relative flex items-center gap-3">
                    {nowPlaying.track.album_art ? (
                      <img src={nowPlaying.track.album_art} alt="" className="w-14 h-14 rounded-xl shadow-[0_10px_25px_-8px_rgba(0,0,0,0.6)] ring-1 ring-white/10" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-[#1db954]/20 flex items-center justify-center"><Music className="w-6 h-6 text-[#1db954]" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{nowPlaying.track.name}</p>
                      <p className="text-white/50 text-xs truncate">{nowPlaying.track.artists}</p>
                      {nowPlaying.track.duration_ms > 0 && (
                        <div className="mt-1.5">
                          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-[width] duration-500 ease-linear"
                              style={{ width: `${Math.min(100, (localProgress / nowPlaying.track.duration_ms) * 100)}%`, background: 'linear-gradient(90deg, #1db954, #0ec963)' }} />
                          </div>
                          <div className="flex justify-between mt-0.5">
                            <span className="text-[9px] text-white/40 tabular-nums">{formatMs(localProgress)}</span>
                            <span className="text-[9px] text-white/40 tabular-nums">{formatMs(nowPlaying.track.duration_ms)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {nowPlaying.track.track_url && (
                    <a href={nowPlaying.track.track_url} target="_blank" rel="noopener noreferrer" data-testid="spotify-track-link"
                      className="relative mt-2 w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#1db954]/15 text-[#1db954] text-[11px] font-medium hover:bg-[#1db954]/25 transition-all">
                      <ExternalLink className="w-3 h-3" /> Play on Spotify
                    </a>
                  )}
                </div>
              )}

              {/* Roblox in-game */}
              {roInGame && (
                <div className="relative overflow-hidden rounded-2xl border border-rose-400/25 p-3"
                  style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.12), rgba(15,10,25,0.4))' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Gamepad2 className="w-3.5 h-3.5 text-rose-300" />
                    <p className="text-[9px] tracking-[0.24em] uppercase font-semibold text-rose-300">Playing Roblox</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {roblox.roblox_avatar_url ? (
                      <img src={roblox.roblox_avatar_url} alt="" className="w-11 h-11 rounded-xl ring-1 ring-white/10" />
                    ) : (
                      <div className="w-11 h-11 rounded-xl bg-rose-500/20 flex items-center justify-center text-white font-bold">R</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{roblox.presence?.last_location || 'In game'}</p>
                      <p className="text-white/50 text-xs truncate">as {roblox.roblox_display_name}</p>
                    </div>
                  </div>
                  {roblox.roblox_profile_url && (
                    <a href={roblox.roblox_profile_url} target="_blank" rel="noopener noreferrer" data-testid="roblox-profile-link"
                      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-rose-500/15 text-rose-300 text-[11px] font-medium hover:bg-rose-500/25 transition-all">
                      <ExternalLink className="w-3 h-3" /> Roblox profile
                    </a>
                  )}
                </div>
              )}

              {/* Custom activity fallback */}
              {!isSpotify && !roInGame && hasActivity && (() => {
                const AI = ACT_ICON[activity.type] || ACT_ICON.custom;
                return (
                  <div className="rounded-2xl border border-fuchsia-400/25 p-3"
                    style={{ background: 'linear-gradient(135deg, rgba(217,70,239,0.10), rgba(15,10,25,0.35))' }} data-testid="custom-activity">
                    <div className="flex items-center gap-2 mb-1">
                      <AI className="w-3.5 h-3.5 text-fuchsia-300" />
                      <p className="text-[9px] tracking-[0.24em] uppercase font-semibold text-fuchsia-300">{activity.type || 'Activity'}</p>
                    </div>
                    {activity.title && <p className="text-white text-sm font-medium truncate">{activity.title}</p>}
                    {activity.detail && <p className="text-white/50 text-xs truncate">{activity.detail}</p>}
                  </div>
                );
              })()}

              {/* About */}
              {profile.bio && (
                <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
                  <p className="text-[9px] tracking-[0.24em] uppercase text-white/40 mb-1.5">About</p>
                  <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
                </div>
              )}

              {/* Work info bento */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
                <p className="text-[9px] tracking-[0.24em] uppercase text-white/40 mb-2">Work info</p>
                <div className="grid grid-cols-2 gap-2">
                  {profile.job_title  && <InfoRow icon={Briefcase} value={profile.job_title} />}
                  {profile.department && <InfoRow icon={Star}      value={profile.department} />}
                  {profile.location   && <InfoRow icon={MapPin}    value={profile.location} />}
                  {profile.work_schedule && <InfoRow icon={Clock}  value={profile.work_schedule} />}
                  <InfoRow icon={Calendar} value={`${profile.leave_days_used || 0} leave days`} />
                </div>
              </div>

              {/* Connected accounts */}
              {(spotifyConnected || roConnected || socials.discord || socials.linkedin || socials.instagram) && (
                <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
                  <p className="text-[9px] tracking-[0.24em] uppercase text-white/40 mb-2">Connections</p>
                  <div className="space-y-1.5">
                    {spotifyConnected && (
                      <SocialRow href={profile.spotify_profile_url || nowPlaying?.spotify_profile_url}
                        hex="#1db954" iconEl={<Music className="w-3.5 h-3.5" style={{ color: '#1db954' }} />}
                        label="Spotify" value={profile.spotify_display_name || (isSpotify && !roInGame ? 'Listening' : 'Connected')}
                        testid="connected-spotify" />
                    )}
                    {roConnected && (
                      <SocialRow href={roblox?.roblox_profile_url} hex="#F43F5E"
                        iconEl={roblox?.roblox_avatar_url ? <img src={roblox.roblox_avatar_url} alt="" className="w-4 h-4 rounded" /> : <Gamepad2 className="w-3.5 h-3.5 text-rose-400" />}
                        label="Roblox" value={roblox?.roblox_display_name || profile.roblox_display_name || profile.roblox_username || (roOnline ? 'Online' : 'Connected')}
                        testid="connected-roblox" />
                    )}
                    {socials.discord && (
                      <SocialRow hex="#5865f2" iconEl={<MessageSquare className="w-3.5 h-3.5" style={{ color: '#5865f2' }} />} label="Discord" value={socials.discord} />
                    )}
                    {socials.instagram && (
                      <SocialRow href={`https://instagram.com/${socials.instagram}`} hex="#ec4899"
                        iconEl={<Instagram className="w-3.5 h-3.5 text-pink-400" />} label="Instagram" value={socials.instagram} />
                    )}
                    {socials.linkedin && (
                      <SocialRow href={socials.linkedin} hex="#0ea5e9"
                        iconEl={<Linkedin className="w-3.5 h-3.5 text-sky-400" />} label="LinkedIn" value="View profile" />
                    )}
                  </div>
                </div>
              )}

              {/* Skills */}
              {profile.skills?.length > 0 && (
                <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
                  <p className="text-[9px] tracking-[0.24em] uppercase text-white/40 mb-2">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.skills.map((s, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border tracking-wide"
                        style={{ color: '#67e8f9', background: 'rgba(0,229,255,0.08)', borderColor: 'rgba(0,229,255,0.25)' }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Message bar */}
            {onMessageClick && (
              <div className="p-3 border-t border-white/[0.06]">
                <button onClick={() => { onMessageClick(profile.username); onClose(); }} data-testid="profile-message-btn"
                  className="group relative w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl overflow-hidden text-black text-sm font-medium transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #00E5FF 0%, #8A2BE2 100%)', boxShadow: '0 12px 30px -8px rgba(0,229,255,0.55)' }}>
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <MessageSquare className="w-4 h-4" /> Message @{profile.username}
                </button>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const InfoRow = ({ icon: Icon, value }) => (
  <div className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/[0.04]">
    <Icon className="w-3.5 h-3.5 text-white/40 flex-shrink-0" strokeWidth={1.8} />
    <span className="text-white/80 text-xs truncate">{value}</span>
  </div>
);

const SocialRow = ({ href, hex, iconEl, label, value, testid }) => {
  const Body = (
    <div className="relative flex items-center gap-2.5 p-2 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.05] transition-all">
      <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl`} style={{ background: hex }} />
      <span className="pl-1">{iconEl}</span>
      <span className="text-white text-xs font-medium flex-1">{label}</span>
      <span className="text-white/50 text-[10px] truncate max-w-[50%]">{value}</span>
      {href && <ExternalLink className="w-3 h-3 text-white/30" />}
    </div>
  );
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}>{Body}</a>
    : <div data-testid={testid}>{Body}</div>;
};

export default StaffProfileCard;
